import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import OperationalWorkflowButton from '../compliance-workflow/OperationalWorkflowButton'
import { executiveGovernanceStandardPath } from '../../services/executiveGovernanceApi'
import type { ExecutiveStandardMiniCard } from '../../services/executiveGovernanceApi'
import type { HomeComplianceStandard } from '../../utils/homeComplianceDisplay'
import type { EffectiveStandardAccess } from '../../services/standardAccessApi'
import {
  formatPercentOrMissing,
  normalizeStability,
  pressureBand,
} from '../../utils/operationalUx'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import { featureFlags } from '../../config/featureFlags'
import styles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type Props = {
  compliance: HomeComplianceStandard
  executive?: ExecutiveStandardMiniCard | null
  scopeLabel?: string
  assetCount?: number | null
  effectiveAccess?: EffectiveStandardAccess | null
  storyLines?: string[]
}

function stabilityBadgeClass(level: string): string {
  const band = normalizeStability(level)
  if (band === 'CRITICAL' || band === 'AT_RISK') return styles.badgeRisk
  if (band === 'DEGRADED' || band === 'WATCH') return styles.badgeWarn
  return styles.badgeStable
}

function pressureBadgeClass(band: string): string {
  if (band === 'CRITICAL') return styles.badgePressureCritical
  if (band === 'HIGH') return styles.badgePressureHigh
  if (band === 'MODERATE') return styles.badgePressureMod
  return styles.badgePressureLow
}

export default function HomeStandardExecutiveCard({
  compliance,
  executive,
  scopeLabel,
  assetCount,
  effectiveAccess,
  storyLines,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const code = compliance.standardCode
  const name = getStandardDisplayName(t, code)
  const stability = executive?.hasEnoughHistory
    ? normalizeStability(executive.certificationRiskLevel)
    : null
  const pressure = executive?.hasEnoughHistory
    ? pressureBand(executive.operationalPressure)
    : null

  const scopeText =
    scopeLabel ||
    (assetCount != null
      ? t('enterpriseOperationalUx.scopeOrgAssets', '{{count}} assets organization-wide', {
          count: String(assetCount),
        })
      : t('enterpriseOperationalUx.scopeOrganizationWide', 'Entire organization'))

  return (
    <article className={styles.standardCard}>
      <header className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{name}</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span className={styles.badgeWarn}>{compliance.statusLabel}</span>
          {stability ? (
            <span className={stabilityBadgeClass(stability)}>{stability}</span>
          ) : null}
        </div>
      </header>

      <div className={styles.complianceRow}>
        <div className={styles.complianceBlock}>
          <p className={styles.complianceLabel}>
            {t('standardGovernance.selfAssessment', 'Self-assessment')}
          </p>
          {compliance.selfPercent != null ? (
            <p className={styles.complianceValue}>
              {formatPercentOrMissing(compliance.selfPercent, '')}
            </p>
          ) : (
            <p className={styles.complianceMissing}>
              {t('standardGovernance.notSelfAssessed', 'Not self-assessed')}
            </p>
          )}
        </div>
        <div className={styles.complianceBlock}>
          <p className={styles.complianceLabel}>
            {t('complianceStatusAuthority.verified.auditReadyLabel', 'Verified / Audit-ready')}
          </p>
          {compliance.verifiedPercent != null ? (
            <p className={styles.complianceValue}>
              {formatPercentOrMissing(compliance.verifiedPercent, '')}
            </p>
          ) : (
            <p className={styles.complianceMissing}>
              {t('complianceStatusAuthority.verified.notAuditReady', 'Not audit-ready')}
            </p>
          )}
        </div>
      </div>

      {executive?.hasEnoughHistory ? (
        <dl className={styles.metricList}>
          <div className={styles.metricRow}>
            <dt>{t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory')}</dt>
            <dd>{executive.readinessTrajectory || '—'}</dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{t('executiveGovernance.operationalPressure', 'Operational Pressure')}</dt>
            <dd>
              {pressure ? (
                <span className={pressureBadgeClass(pressure)}>
                  {pressure} · {executive.operationalPressure ?? 0}
                </span>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div className={styles.metricRow}>
            <dt>{t('executiveGovernance.degradedControls', 'degraded controls')}</dt>
            <dd>{executive.degradedControlCount ?? 0}</dd>
          </div>
          {executive.staleEvidenceCount != null ? (
            <div className={styles.metricRow}>
              <dt>{t('executiveGovernance.staleEvidence', 'Stale evidence')}</dt>
              <dd>{executive.staleEvidenceCount}</dd>
            </div>
          ) : null}
          <div className={styles.metricRow}>
            <dt>{t('executiveGovernance.blockers', 'Blockers')}</dt>
            <dd>{executive.blockerCount ?? 0}</dd>
          </div>
        </dl>
      ) : (
        <p className={styles.emptyState}>
          {t('executiveGovernance.notEnoughExecutiveHistory', 'Not enough executive history yet')}
        </p>
      )}

      {storyLines && storyLines.length > 0 ? (
        <p className={styles.postureStory} style={{ marginTop: 0, fontSize: 13 }}>
          {storyLines.join(' · ')}
        </p>
      ) : null}

      <p className={styles.scopeLine}>
        {t('enterpriseOperationalUx.scope', 'Scope')}: {scopeText}
      </p>

      <div className={styles.cardActions}>
        <OperationalWorkflowButton standardCode={code} effectiveAccess={effectiveAccess} size="small" />
        <Button
          type="button"
          size="small"
          color="outline"
          onClick={() => navigate(executiveGovernanceStandardPath(code))}
        >
          {t('executiveGovernance.openExecutiveDrilldown', 'Open executive drill-down')}
        </Button>
        {featureFlags.COMPLIANCE_RUNTIME_V1 ? (
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => navigate('/compliance-center/operational-events')}
          >
            {t('enterpriseOperationalUx.openRuntimeEvents', 'Runtime events')}
          </Button>
        ) : null}
        <Button
          type="button"
          size="small"
          color="outline"
          onClick={() => navigate(`/compliance?standard=${encodeURIComponent(code)}`)}
        >
          {t('enterpriseOperationalUx.openApplicability', 'Applicability')}
        </Button>
      </div>
    </article>
  )
}
